import { useEffect, useEffectEvent, useRef } from 'react'
import { MarkdownEditor, Mtds } from 'prove-common'

export function NewsMarkdownField({
  value,
  onChange,
  onPendingChange,
  disabled,
  error,
  charLimit
}: {
  value: string
  onChange: (value: string) => void
  onPendingChange: (pending: boolean) => void
  disabled: boolean
  error?: string
  charLimit: number
}) {
  const fieldRef = useRef<HTMLFieldSetElement>(null)
  const serializedHtml = useRef<string | undefined>(undefined)
  const checkContent = useEffectEvent(() => {
    const editor =
      fieldRef.current?.querySelector<HTMLElement>('[contenteditable]')
    if (!editor || disabled) return
    serializedHtml.current ??= editor.innerHTML
    onPendingChange(editor.innerHTML !== serializedHtml.current)
  })
  const captureEdit = (event: React.SyntheticEvent) => {
    if (
      disabled ||
      !(event.target instanceof Element) ||
      !event.target.closest('[contenteditable]')
    )
      return
    onPendingChange(true)
    // A cancelled/no-op paste may not produce a DOM mutation or Markdown callback.
    queueMicrotask(() => {
      const editor =
        fieldRef.current?.querySelector<HTMLElement>('[contenteditable]')
      if (editor) onPendingChange(editor.innerHTML !== serializedHtml.current)
    })
  }
  useEffect(() => {
    const field = fieldRef.current
    if (!field) return
    // The shared editor mounts asynchronously and does not expose ARIA props.
    const labelEditor = () => {
      const editor = field.querySelector<HTMLElement>('[contenteditable]')
      if (!editor) return
      editor.setAttribute('role', 'textbox')
      editor.setAttribute('aria-labelledby', 'news-subject-label')
      editor.setAttribute(
        'aria-describedby',
        error ? 'news-subject-help news-subject-error' : 'news-subject-help'
      )
      editor.setAttribute('aria-multiline', 'true')
      editor.setAttribute('aria-required', 'true')
      editor.setAttribute('aria-invalid', String(!!error))
      editor.setAttribute('aria-disabled', String(disabled))
    }
    labelEditor()
    checkContent()
    const observer = new MutationObserver(() => {
      labelEditor()
      checkContent()
    })
    observer.observe(field, {
      childList: true,
      characterData: true,
      subtree: true
    })
    return () => observer.disconnect()
  }, [disabled, error])

  return (
    <Mtds.Fieldset
      ref={fieldRef}
      id='news-subject'
      tabIndex={-1}
      disabled={disabled}
      onInputCapture={captureEdit}
      onPasteCapture={captureEdit}
      onCutCapture={captureEdit}
    >
      <Mtds.Fieldset.Legend id='news-subject-label'>
        Innhold (Markdown)
      </Mtds.Fieldset.Legend>
      <Mtds.Fieldset.Description id='news-subject-help'>
        Skriv og formater teksten med verktøylinjen, eller lim inn formatert
        tekst. Maksimalt {charLimit} tegn.
      </Mtds.Fieldset.Description>
      <Mtds.Grid inert={disabled}>
        <MarkdownEditor
          value={value}
          onChange={(next) => {
            if (disabled) return
            serializedHtml.current =
              fieldRef.current?.querySelector<HTMLElement>(
                '[contenteditable]'
              )?.innerHTML
            onChange(next)
            onPendingChange(false)
          }}
          disabled={disabled}
          charLimit={charLimit}
          placeholder='Skriv innhold for nyhetsartikkelen...'
        />
      </Mtds.Grid>
      {error && (
        <Mtds.Validation id='news-subject-error'>{error}</Mtds.Validation>
      )}
    </Mtds.Fieldset>
  )
}
